<template>
  <div>
    <div v-if="isLoadingData">
      <Spinner />
    </div>
    <div v-else-if="!orderDetails || !orderDetails.order_number">
      {{$t('INDENT_NO_DOCUMENT_DATA')}}
    </div>
    <div
      v-else-if="orderDetails"
      class="panel-card card-lg clearfix"
      v-bind:class="{'slider-open': (viewAttachmentsDetailsSlider || viewLRSummarySlider)}"
    >
      <div class="card-left">
        <p class="text-sm mt-0 text-red1" v-if="orderDetails && isOverLoaded">
          <span v-if="originalTruckWeightVolume.weight < totalMaterial.weight"> {{$t('INDENT_TRUCK_OVERLOADED_BY')}} : {{ totalMaterial.weight - originalTruckWeightVolume.weight }} {{$t('INDENT_KG')}} <br> {{$t('INDENT_ORIGINAL_TRUCK_WEIGHT')}}: {{originalTruckWeightVolume.weight}} {{$t('INDENT_KG')}}</span><br>
          <span v-if="originalTruckWeightVolume.volume < totalMaterial.volume"> {{$t('INDENT_TRUCK_OVERLOADED_BY')}} : {{ computeVolumeCalculationAndUnit.value }} {{ computeVolumeCalculationAndUnit.unit }} <br> {{$t('INDENT_ORIGINAL_TRUCK_VOLUME')}}: {{originalTruckWeightVolume.volume}} {{ preferredVolumeUnit}}</span>
        </p>
        <div
          class="card-list is-attachment"
          v-for="(destination,index) in destinations"
          :key="index"
          :class="{'active': (`${selectedConsignee.details.id}${selectedConsignee.sold_to_id}` === `${destination.details.id}${destination.sold_to_id}`) &&
           selectedConsignee.gate_id === destination.gate_id}"
          @click="onChangeConsignee(destination)"
        >
          <div class="list-top">
            <div class="d-flex">
              <i class="icon-source">
                <svgicon name="icon-vehicle-md" width="14" height="14" />
              </i>
              <span class="title">
                {{ getSourceConsigneeDetails(destination) }}
                <small>{{getConsigneeDetails(destination)}}</small>
                <Tooltip :open-delay="100" effect="dark" placement="bottom-start" v-if="otpVerified(destination.location_id) || destination.pod_approved">
                  <!-- <div slot="content">Invalid Material Invoice</div>
                  <span class="info text-red1">
                    <svgicon name="warning" width="16" height="16" />
                  </span> -->
                  <div slot="content" v-if="otpVerified(destination.location_id)">{{$t('INDENT_OTP_SUBMIT_SUCCESS')}}</div>
                  <div slot="content" v-if="destination.pod_approved">{{$t('INDENT_POD_APPROVAL')}}</div>
                  <span class="info text-green2">
                    <svgicon name="icon-tick-circle" width="16" height="16" />
                  </span>
                </Tooltip>
              </span>
              <span v-if="destination.is_not_delivered" class="badge badge-bottom">
                <svgicon name="icon-warning-circle" class="f-right" width="12" height="12" :original="true"/>
                <svgicon name="not-delivered" width="67" height="14" :original="true"/>
              </span>
            </div>
            <div class="d-flex mt-10" @click.stop>
              <AttachmentsMin
                :attachments="getLrAttachmentsByDestination(destination)"
                :disableUpload="isDisableUpload"
                :maxAllowed="lrAttachmentsLimit"
                :multiple=true
                :use-modal-upload="isConsolidatedEnabled"
                @setGA="$emit('setGA','Click',$event)"
                @addAttachments="addAttachments($event, destination)"
                @openPreviewSlider="openPreviewSlider(destination)"
              ></AttachmentsMin>
            </div>
          </div>
          <lr-number-input
          v-if="!isOrderLoading"
            :destination="destination"
            :lrSummary="getLrAttachmentsByDestination(destination)"
            :isSelectedConsignee="selectedConsignee.details.id === destination.details.id"
            :canUpdateLrNumber="canUpdateLrNumber"
            :canUpdateMaterialInvoiceLR="canUpdateMaterialInvoiceLR"
            :legalEntityDetails="legalEntityDetails"
            :canShowConsignmentNumber="canShowConsignmentNumber"
            :hideConsignmentEdit="hideConsignmentEdit"
            :hasMaterials="hasMaterialsByDestinationId(destination)"
            @loadLrDetails="getLrDetails"
            @loadConsignmentDetails="getTransporterBooking"
          ></lr-number-input>
          <consignment-number-input
            v-if="canShowConsignmentNumber"
            :consgtNumberDetailsForConsignee="getConsignmentNumber(destination)"
            :destination="destination"
            :isSelectedConsignee="selectedConsignee.details.id === destination.details.id"
            :hideConsignmentEdit="hideConsignmentEdit"
            :legalEntityDetails="legalEntityDetails"
            :isMultiDropGateConfig="isMultiDropGateConfig"
            @loadConsignmentDetails="getTransporterBooking"
          ></consignment-number-input>
          <div
            v-if="$hasFeature('enable_destination_pod_approval') && destination.pod_approved"
            class="pod-ai-approved-tag"
          >
            <svgicon name="icon-tick-circle" width="14" height="14" />
            <span>{{ $t('POD_AI_APPROVED_MANUALLY_TAG') }}</span>
          </div>
          <template v-else-if="$hasFeature('pod_rejection_by_ai')">
            <div
              v-if="getPodAiDestStatus(destination) === 'AI_REJECTED'"
              class="pod-ai-rejected-tag"
              @click.stop="openPodAiSlider(destination)"
            >
              <svgicon name="icon-warning-circle" width="14" height="14" :original="true" />
              <span>{{ $t('POD_AI_REJECTED_TAG') }}</span>
            </div>
            <div
              v-else-if="getPodAiDestStatus(destination) === 'AI_APPROVED'"
              class="pod-ai-approved-tag"
            >
              <svgicon name="icon-tick-circle" width="14" height="14" />
              <span>{{ $t('POD_AI_ACCEPTED_TAG') }}</span>
            </div>
          </template>
        </div>
        <Drawer
          :visible.sync="viewAttachmentsDetailsSlider"
          direction="rtl"
          size="640px"
          :modal="false"
          modal-wrapper="false"
          :append-to-body="true"
          :withHeader="false"
          class="default-drawer dark"
        >
          <Link
            :underline="false"
            class="nav-close"
            @click="viewAttachmentsDetailsSlider=false"
          >
            <svgicon name="cancelwhite" width="12" height="12" :original="true" />
          </Link>
          <div class="drawer-body">
            <AttachmentsPreview
              v-if="viewAttachmentsDetailsSlider"
              :lrNumberDetails="getLrNumberDetails(selectedConsignee)"
              :attachments="getLrAttachmentsByDestination(selectedConsignee)"
              :disableUpload="isDisableUpload"
              :disableDelete="isDisableDelete"
              :maxAllowed="lrAttachmentsLimit"
              :multiple=true
              :orderDetails="orderDetails"
              @setGA="$emit('setGA','Click',$event)"
              @addAttachments="addAttachments"
              @removeAttachments="removeAttachments"
              @rejectAttachments="rejectAttachments"
            ></AttachmentsPreview>
          </div>
        </Drawer>
        <Drawer
          :visible.sync="viewLRSummarySlider"
          direction="rtl"
          size="640px"
          :modal="false"
          modal-wrapper="false"
          :append-to-body="true"
          :withHeader="false"
          class="default-drawer dark"
        >
          <Link
            :underline="false"
            class="nav-close"
            @click="viewLRSummarySlider=false"
          >
            <svgicon name="cancelwhite" width="12" height="12" :original="true" />
          </Link>
          <div class="drawer-body p-0">
            <AttachmentsPreviewV2
              v-if="viewLRSummarySlider"
              :viewLrSummary="viewLRSummarySlider"
              :summary="indentSummary"
              :attachments="getLrAttachmentsByDestination()"
              :disableUpload="true"
              :disableDelete="true"
              :maxAllowed="lrAttachmentsLimit"
              :multiple=true
              :orderDetails="orderDetails"
              @setGA="$emit('setGA','Click',$event)"
            ></AttachmentsPreviewV2>
          </div>
        </Drawer>
        <!-- POD AI Validation Slider -->
        <Drawer
          v-if="$hasFeature('pod_rejection_by_ai')"
          :visible.sync="viewPodAiSlider"
          direction="rtl"
          size="640px"
          :modal="false"
          modal-wrapper="false"
          :append-to-body="true"
          :withHeader="false"
          class="default-drawer dark"
        >
          <Link :underline="false" class="nav-close" @click="viewPodAiSlider = false">
            <svgicon name="cancelwhite" width="12" height="12" :original="true" />
          </Link>
          <div class="drawer-body" v-if="podAiSliderDestination">
            <h3 class="text-white mb-15">{{ podAiSliderDestination.details && podAiSliderDestination.details.name }}</h3>
            <div class="pod-ai-validation-box mb-20">
              <div class="pod-ai-validation-header mb-5">
                <svgicon name="icon-info-circle" width="16" height="16" :original="true" />
                <strong class="ml-5">POD Validation</strong>
              </div>
              <p class="text-sm mt-0 mb-10" style="color: #aaa;">This POD failed AI validation. Fix the issues below and upload the document again.</p>
              <div
                v-for="reason in getPodAiRejectionReasons(podAiSliderDestination)"
                :key="reason.code"
                class="pod-ai-reason-item"
              >
                <svgicon name="icon-warning-circle" width="14" height="14" :original="true" />
                <span class="ml-5">{{ reason.label }}</span>
              </div>
            </div>
            <p class="text-xs text-muted mb-10" style="letter-spacing: 0.5px; text-transform: uppercase;">Uploaded Document</p>
            <AttachmentsPreview
              v-if="viewPodAiSlider"
              :lrNumberDetails="getLrNumberDetails(podAiSliderDestination)"
              :attachments="getLrAttachmentsByDestination(podAiSliderDestination)"
              :disableUpload="isDisableUpload"
              :disableDelete="isDisableDelete"
              :maxAllowed="lrAttachmentsLimit"
              :multiple="true"
              :orderDetails="orderDetails"
              @setGA="$emit('setGA','Click',$event)"
              @addAttachments="addAttachments"
              @removeAttachments="removeAttachments"
              @rejectAttachments="rejectAttachments"
            ></AttachmentsPreview>
          </div>
        </Drawer>
      </div>
      <div class="card-right" v-if="!isMaterialLoading" style="height: calc(100% - 160px);">
        <div class="d-flex items-center" style="background-color: #FFEEC4; margin-bottom: 0.5;"
        v-if="selectedConsignee.type === types.DEPOT && showComplaints && showComplaintsCreateView">
          <div style="margin-top: 1rem; margin-left: 1rem; margin-right: 0.5rem;">
          <svgicon name="icon-info-circle" :original="true" />
          </div>
          <div>
            <h4 v-html="getDataInterpolatedText($t('COMPLAINT_SUB_WNDW_MSSG_HEADER'), { timestamp: formatDate })"></h4>
            <p>{{  $t('COMPLAINT_SUB_WNDW_MSSG_PARA') }}</p>
          </div>
        </div>
        <div class="card-content d-block p-10  pb-100">
          <div class="flex-container panel-head align-top mt-m10">
            <div class="flex-item">
              <p>
                {{$t('INDENT_SHIP_TO')}} :
                <strong
                  class="text-bold"
                  v-if="selectedConsignee.details"
                >{{ shipToBasedOnLRCategory }}</strong>
              </p>
              <p>
                {{$t('INDENT_SOLD_TO')}} :
                <strong
                  class="text-bold"
                  v-if="selectedConsignee.details"
                >{{ soldToBasedOnLRCategory }}</strong>
              </p>
              <p v-if="getRejectReason" class="text-red">
                {{$t('INDENT_REJECTION_REASON')}} :
                <strong class="text-bold">
                  {{ getRejectReason }}
                </strong>
              </p>
              <template v-if="deliveryNumberList && deliveryNumberList.length">
                <p
                  v-for="(gate,index) in deliveryNumberList"
                  :key="index"
                >
                  {{gate && gate.name}} :
                  <strong
                    class="text-bold"
                    v-if="gate && gate.deliveryNumbers"
                  >{{gate && gate.deliveryNumbers}}</strong>
                  <span v-else>-</span>
                </p>
              </template>
            </div>
            <div class="flex-item text-right ml-auto">
               <div v-if="showMaterialInvoiceOTP">
                <strong
                    class="text-bold"
                  >OTP
                  </strong>
                  <Tooltip :open-delay="200" effect="dark" placement="bottom">
                    <div slot="content">
                      Transporter Mobile No : {{ this.orderDetails.transporter.mobile_number }} <br/><br/>
                      Driver Mobile No : {{ this.orderDetails.driver.mobile }}
                    </div>
                    <span class="info pointer">
                      <svgicon name="icon-info-circle" width="19" height="19" :original="true" />
                    </span>
                  </Tooltip>
                </div>
              <div v-else-if="canEnterOtp() && !otpVerified(selectedConsignee.location_id)">
                <Tooltip :open-delay="200" effect="dark" placement="bottom">
                  <div slot="content">
                    {{$t('INDENT_TRANSPORTER_MOBILE_NUMBER')}} : {{ this.orderDetails.transporter.mobile_number }} <br/><br/>
                    {{$t('INDENT_DRIVER_MOBILE_NO')}} : {{ this.orderDetails.driver.mobile }}
                  </div>
                  <span class="info pointer">
                    <svgicon name="icon-info-circle" width="19" height="19" :original="true" />
                  </span>
                </Tooltip>
                <span>
                  <Button type="primary" size="mini" class="mt-10 ml-5" @click="showOtpPopup = true">{{$t('INDENT_ENTER_OTP')}}</Button>
                </span>
              </div>
              <p v-if="canEnterOtp() && otpVerified(selectedConsignee.location_id)">
                <svgicon name="icon-tick-circle" class="text-green2" width="16" height="16" />
                {{$t('INDENT_OTP_SUBMITTED')}}
              </p>
              <Dialog
                width="300px"
                middle="true"
                :center="false"
                title="Enter OTP"
                :append-to-body="true"
                :visible.sync="showOtpPopup"
                class="default-dialog"
              >
                <div class="dialog-content">
                  {{$t('INDENT_OTP_ENTER_FOR_GRN_RELEASE')}}
                  <Form :inline="true" class="form-default mt-10">
                    <FormItem label="OTP">
                      <Input :placeholder="$t('INDENT_ENTER_OTP')" type="text" v-model="otp"></Input>
                    </FormItem>
                  </Form>
                  <p>
                    <Button type="text" class="button-link text-link" size="mini" @click="resendOtp(selectedConsignee.location_id)">{{$t('INDENT_RESEND_OTP')}}</Button>
                  </p>
                </div>
                <span slot="footer" class="dialog-footer">
                  <Button type="secondary" size="mini" @click="showOtpPopup = false">{{$t('INDENT_CANCEL')}}</Button>
                  <Button type="primary" size="mini" @click="submitOtp(selectedConsignee.location_id)">{{$t('INDENT_SUBMIT')}}</Button>
                </span>
              </Dialog>

              <p class="list-badge">
                <span v-if="showActualMaterials">
                <span class="badge">{{$t('ACTUAL_INDENT_WEIGHT')}}</span>
                <label>
                  <template
                    v-if="actualSummaryByDestinationId && actualSummaryByDestinationId.total_weight && actualSummaryByDestinationId.total_weight.value >0"
                  >{{fixTwoDigits(actualSummaryByDestinationId.total_weight.value)}} {{materialsSummaryByDestinationId.total_weight ? materialsSummaryByDestinationId.total_weight.unit : 'kg' }}</template>
                  <template
                    v-else-if="materialsSummaryByDestinationId.total_weight"
                  >{{fixTwoDigits(materialsSummaryByDestinationId.total_weight.value)}} {{materialsSummaryByDestinationId.total_weight.unit}}</template>
                  <template v-else>0</template>
                </label>
                <span class="badge">{{$t('ACTUAL_INDENT_VOLUME')}}</span>
                <label>
                  <template
                    v-if="actualSummaryByDestinationId && actualSummaryByDestinationId.total_volume && actualSummaryByDestinationId.total_volume.value > 0"
                  >{{fixTwoDigits(getConvertedClientVolume(actualSummaryByDestinationId.total_volume.value))}} {{preferredVolumeUnit.toLowerCase()}}</template>
                  <template
                    v-else-if="materialsSummaryByDestinationId.total_volume"
                  >{{fixTwoDigits(materialsSummaryByDestinationId.total_volume.value)}} {{materialsSummaryByDestinationId.total_volume.unit}}</template>
                  <template v-else>0</template>
                </label>
                <span class="badge">{{$t('ACTUAL_INDENT_QUANTITY')}}</span>
                <label>
                  <template v-if="actualSummaryByDestinationId && actualSummaryByDestinationId.total_quantity_nos && actualSummaryByDestinationId.total_quantity_cs">
                    <span v-if="actualSummaryByDestinationId.total_quantity_nos && actualSummaryByDestinationId.total_quantity_nos.value">
                      {{fixTwoDigits(
                        actualSummaryByDestinationId.total_quantity_nos.value
                      )}}
                      {{ actualSummaryByDestinationId.total_quantity_nos.unit }}
                    </span>
                    <span
                      v-if="actualSummaryByDestinationId.
                          total_quantity_cs && actualSummaryByDestinationId.total_quantity_cs.value"
                    >
                    {{fixTwoDigits(
                      actualSummaryByDestinationId.total_quantity_cs.value
                      )}}
                      {{actualSummaryByDestinationId.total_quantity_cs.unit}}
                    </span>
                  </template>
                  <template v-else-if="materialsSummaryByDestinationId.total_quantity_nos">
                    <span v-if="materialsSummaryByDestinationId.total_quantity_nos.value">
                      {{fixTwoDigits(
                      materialsSummaryByDestinationId.total_quantity_nos.value
                      )}}
                      {{materialsSummaryByDestinationId.total_quantity_nos.unit}}
                    </span>
                    <span
                      v-if="materialsSummaryByDestinationId.
                          total_quantity_cs.value"
                    >
                      {{fixTwoDigits(
                      materialsSummaryByDestinationId.total_quantity_cs.value
                      )}}
                      {{materialsSummaryByDestinationId.total_quantity_cs.unit}}
                    </span>
                  </template>
                  <template v-else>0</template>
                </label>
              </span>
                <span class="badge">{{$t('INDENT_WEIGHT')}}</span>
                <label>
                  <template
                    v-if="materialsSummaryByDestinationId.total_weight"
                  >{{fixTwoDigits(materialsSummaryByDestinationId.total_weight.value)}} {{materialsSummaryByDestinationId.total_weight.unit}}</template>
                  <template v-else>0</template>
                </label>
                <span class="badge">{{$t('INDENT_VOLUME')}}</span>
                <label>
                  <template
                    v-if="materialsSummaryByDestinationId.total_volume"
                  >{{fixTwoDigits(getConvertedClientVolume(materialsSummaryByDestinationId.total_volume.value))}} {{preferredVolumeUnit.toLowerCase()}}</template>
                  <template v-else>0</template>
                </label>
                <span class="badge">{{$t('INDENT_QUANTITY')}}</span>
                <label>
                  <template v-if="materialsSummaryByDestinationId.total_quantity_nos">
                    <span v-if="materialsSummaryByDestinationId.total_quantity_nos.value">
                      {{fixTwoDigits(
                      materialsSummaryByDestinationId.total_quantity_nos.value
                      )}}
                      {{materialsSummaryByDestinationId.total_quantity_nos.unit}}
                    </span>
                    <span
                      v-if="materialsSummaryByDestinationId.
                          total_quantity_cs.value"
                    >
                      {{fixTwoDigits(
                      materialsSummaryByDestinationId.total_quantity_cs.value
                      )}}
                      {{materialsSummaryByDestinationId.total_quantity_cs.unit}}
                    </span>
                  </template>
                  <template v-else>0</template>
                </label>
              </p>
              <p class="list-badge">
                <span v-if="currentDestination && currentDestination.collection_amount" class="text-xs status" :class="getClass">
                  {{ $t('CASH_ON_DELIVERY') }} :
                  <svgicon :name="getSvgIconIndent" width="10" height="10" :original="true" />
                  <strong class="text-bold">{{ currentClient.currency && currentDestination.collection_amount ?
                  $getCurrencyFormat(currentDestination.collection_amount, currentClient.currency) : $getIndianFormat(currentDestination.collection_amount)}}</strong>
                </span>
                <span v-if="currentDestination.collection_amount" class="ml-4">
                  <Tooltip :open-delay="200" effect="dark" placement="bottom-start">
                    <div v-if="!currentDestination.is_cash_collected" slot="content">{{ $t('INDENT_YET_TO_COLLECT_CASH') }}</div>
                    <div v-else slot="content">{{ $t('INDENT_CASH_COLLECTED') }}</div>
                    <svgicon name="icon-info-circle" width="14" height="14" :original="true" />
                  </Tooltip>
                </span>
                <span class="ml-5">
                  {{$t('INDENT_INVOICE_VALUE')}} :
                  <strong class="text-bold">{{currentClient.currency}}
                  {{currentClient.currency
                  ? $getCurrencyFormat(invoiceAmount, currentClient.currency) : $getIndianFormat(invoiceAmount)}}</strong>
                </span>
              </p>
            </div>
          </div>
          <delivery-numbers
            v-if="showEditDeliveryNumbers"
            :gatesList="gatesList"
            :destination="selectedConsignee"
            :dropGatesList="dropGatesList"
            :soldToIdMap="soldToIdMap"
            :order="orderDetails"
          ></delivery-numbers>
          <div v-if="orderDetails.status === 23 && $hasPermission('approve_pod') && $hasFeature('enable_destination_pod_approval')" class="flex-item text-right ml-auto mt-10 mb-10">
              <Button size="mini" type="secondary" :disabled="selectedConsignee.pod_approved" @click="approveDestinationPOD" style="margin: 0 1rem;">{{ $t('APPROVE_POD')}}</Button>
          </div>
          <div class="status-msg my-xs" v-if="!hasMaterials">
            <span>
              <svgicon name="empty-materials" width="250" height="120" :original="true" />
              <p>{{$t('INDENT_NO_MATERIAL_UPLOADED')}}</p>
            </span>
          </div>
          <material-details
            :selectedConsignee="selectedConsignee"
            :viewAttachmentsDetailsSlider="viewAttachmentsDetailsSlider"
            :materialsList="materialsList"
            :canShowGrn="showGrn"
            :canEditGrn="editGrn"
            :canEPODEditAble="canEPODEditAble"
            :legalEntityDetails="legalEntityDetails"
            :unsavedReturnMaterialDetails="unsavedReturnMaterialDetails"
            :isReturnMaterialsEnabled="isReturnMaterialsEnabled"
            :isDivisionGroupingDisabled="isDivisionGroupingDisabled"
            ref="material_details"
            @fetchMaterials="fetchMaterials"
            @epodRejected="$emit('epodRejected')"
            @epodRejectReason="epodRejectReason"
            @updateSelectedConsignee="updateSelectedConsignee"
            :approve="approve"
            ></material-details>
          <Collapse accordion class="collapse-slider mt-15" v-if="showMaterialAccess">
              <CollapseItem :title="'EXCESS MATERIAL DETAIL'" name="1">
              <MaterialExcess
              ref="material_excess"
              :selectedConsignee="selectedConsignee"
              :excessMaterials="excessMaterials"
              @refetch-excess-materials="getMaterialExcess"
              :materialHeaders="materialHeaders"
              :orderDetails="orderDetails"
              :isShipper="true"
              ></MaterialExcess>
            </CollapseItem>
          </Collapse>
          <component
            v-if="showComplaints"
            :key="selectedConsignee.unique_id"
            :is="componentToShow"
            :complaintsForCurrentDrop="complaintsForCurrentDrop"
            :selectedConsignee="selectedConsignee"
            :orderDetails="orderDetails"
            :materialsList="materialsList"
          />
        </div>
      </div>
    </div>
    <UploadDocumentModal
      :visible="uploadModalVisible"
      :destination="activeDestination || {}"
      :order-no="orderNo || ''"
      :movement="movement || 0"
      @close="uploadModalVisible = false"
      @uploaded="onAttachmentUploaded"
    />
    <Drawer
      v-if="isConsolidatedEnabled"
      :visible.sync="previewDrawerVisible"
      direction="rtl"
      size="600px"
      :modal="true"
      modal-wrapper="false"
      :append-to-body="true"
      :withHeader="false"
      class="default-drawer dark"
    >
      <Link :underline="false" class="nav-close" @click="previewDrawerVisible = false">
        <svgicon name="cancelwhite" width="12" height="12" :original="true" />
      </Link>
      <div class="drawer-body">
        <DocumentPreviewDrawer
          v-if="previewDrawerVisible"
          ref="documentPreviewDrawer"
          :destinations="destinations"
          :destination-index="activeDestinationIndex"
          :order-no="orderNo || ''"
          :movement="movement || 0"
          :disable-upload="isDisableUpload"
          :lr-number-details="activeDestination ? getLrNumberDetails(activeDestination) : []"
          @deleted="onAttachmentDeleted"
          @add-attachment="onDrawerAddAttachment"
        />
      </div>
    </Drawer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { uniqBy, uniq, groupBy, isEmpty, isBoolean } from 'lodash';
import moment from 'moment';
import { sourceAndDestinationType,
  LorryReceiptGenerationCategory,
  deliveryPendingReasonsMapping,
  movementType, orderType, orderDestinationType,
  auditPlatform, deliveryTypes, quantityUnitsForPieces, VolumeUnits, InvertedMovements } from 'pando-constants';

import AttachmentConstants from '@common/constants/AttachmentConstants';
import AttachmentsMin from '@common/components/AttachmentsMin';
import AttachmentsPreview from '@common/components/AttachmentsPreview';
import AttachmentsPreviewV2 from '@common/components/AttachmentsPreviewV2';
import LrNumberInput from '@/modules-v2/indent/views/document/lr-number-input';
import MaterialDetails from '@/modules-v2/indent/views/document/material-details';
import MaterialExcess from '@common/components/material-excesss';
import ComplaintDetails from '@/modules-v2/indent/views/document/complaint-details';
import ComplaintViewDetails from '@/modules-v2/indent/views/document/complaint-view-details';
import UploadDocumentModal from '@/modules-v2/indent/views/document/UploadDocumentModal';
import DocumentPreviewDrawer from '@/modules-v2/indent/views/document/DocumentPreviewDrawer';
import DeliveryNumbers from '@/modules-v2/indent/views/document/delivery-numbers';
import TableCell from '@/components/orders/documentation/TableCell';
import ConsignmentNumberInput from '@/modules-v2/indent/views/document/consignment-number-input';
import LrNumberListShipperMixin from '@/modules-v2/indent/mixins/document/LrNumberListShipperMixin';
import OrderUtils from '../../../../utils/order';
import { quantityUnits, units } from '@common/constants/grnConstants';
import { Menu } from '@/constants/order';
import VolumeFormulaConvertor from '../../../../../../common-v2/helpers/volume-formula-converter';


export default {
  props: ['showGrn', 'approve' , 'editGrn', 'showEditDeliveryNumbers', 'legalEntityDetails',
    'isReturnMaterialsEnabled', 'unsavedReturnMaterialDetails', 'returnMaterialWarnedOnce'],
  components: {
    AttachmentsMin,
    AttachmentsPreview,
    AttachmentsPreviewV2,
    TableCell,
    LrNumberInput,
    MaterialDetails,
    MaterialExcess,
    ConsignmentNumberInput,
    DeliveryNumbers,
    ComplaintDetails,
    ComplaintViewDetails,
    UploadDocumentModal,
    DocumentPreviewDrawer,
  },
  mixins: [LrNumberListShipperMixin],
  watch: {
    showComplaints(val) {
      if (val) {
        this.fetchComplaints();
      }
    },
  },
  data() {
    return {
      viewAttachmentsDetailsSlider: false,
      uploadModalVisible: false,
      previewDrawerVisible: false,
      activeDestination: null,
      activeDestinationIndex: 0,
      orderType,
      deliveryTypes,
      quantityUnitsForPieces,
      // current selected consignee
      selectedConsignee: {},
      attachments: new Map(),
      lrAttachmentsObj: {},
      canEPODEditAble: true,

      // constants
      AttachmentConstants,
      sourceAndDestinationType,

      isLoadingData: false,
      Menu,
      showOtpPopup: false,
      otp: null,
      defaultSection: { name: null },
      sectionType: { DEPOT: 1 },
      rejectedReason: '',
      isEximIndent: false,
      types: { DEPOT: 1, CONSIGNEE: 2, VENDOR_DEPOT: 3 },
      qtyUnits: {},
      viewLRSummarySlider: false,
      excessMaterials: [],
      viewPodAiSlider: false,
      podAiSliderDestination: null,
      podAiValidationStatus: null,
    };
  },
  async created() {
    this.isLoadingData = true;
    this.category = this.$route.params.category;
    // fetch order details for source and destination details
    try {
      const result = await this.$store.dispatch('order/find', this.$route.params.orderId);
      this.getMaterialExcess();
      // fetch the material headers based on client
      if (result && result.order_number) {
        const destinations = result.destinations;
        if (result.exim_type) {
          this.isEximIndent = true;
        }
        // check for disable Approve Button
        destinations.forEach((destination) => {
          if (destination.epod_rejected) {
            this.$emit('disableApproveButton');
          }
          if (destination.epod_rejected && destination.is_transporter_updated) {
            this.$emit('showApproveButton');
          }
        });
        await this.$store.dispatch('order/materialHeaders');
        await this.fetchMaterials();
        this.$store.commit('order/SET_CAN_SHOW_SAVE_GRN', true);
        this.selectedConsignee = this.destinations && this.destinations[0];

        if (!this.selectedConsignee.is_transporter_updated &&
          this.selectedConsignee.epod_rejected && this.selectedConsignee.epod_rejected.reason) {
          this.rejectedReason = this.selectedConsignee.epod_rejected.reason;
        } else {
          this.rejectedReason = '';
        }
        await this.getLrDetails();
        await this.getLrAttachments();
        await this.refreshV2AttachmentsCount();
        this.showSaveButton();
        this.$store.commit('order/CLEAR_GRN_DETAIL', []);
        this.$store.commit('order/SET_DELIVERY_NUMBER', []);
        await this.getTransporterBooking();
        if (this.$hasFeature('pod_rejection_by_ai')) {
          this.fetchPodAiValidationStatus(); // non-blocking, don't await
        }
      }
    } catch (error) {
      this.$error({ message: 'Unable to load data' });
    }
    this.isLoadingData = false;
  },
  computed: {
    isConsolidatedEnabled() {
      return this.$hasFeature('consolidated_attachment_management');
    },
    orderNo() {
      return this.orderDetails && this.orderDetails.order_number;
    },
    movement() {
      return this.orderDetails && this.orderDetails.movement;
    },
    ...mapGetters({
      orderDetails: 'order/find',
      isOrderLoading: 'order/isOrderLoading',
      configurations: 'app/configurations',
      filter: 'order/filter',

      isMaterialLoading: 'order/isMaterialLoading',
      allMaterials: 'order/materials',
      materialHeaders: 'order/materialHeaders',

      grnDetails: 'order/grnDetails',
      userEdited: 'order/userEdited',
      warnedOnce: 'order/warnedOnce',
      depots: 'app/otherDepots',
      currentUserDepots: 'app/depots',
      transporterBookingData: 'order/trackingList',
      currentClient: 'app/currentClient',
      indentSummary: 'order/indentSummary',
      currentUser: 'app/currentUser',
      hasUnsavedComplaints: 'order/hasUnsavedComplaints',
      unsavedComplaintsWarnedOnce: 'order/unsavedComplaintsWarnedOnce',
      allComplaints: 'order/complaints',
    }),
    componentToShow() {
      if (this.showComplaintsCreateView) {
        return 'ComplaintDetails';
      } else if (this.complaintsForCurrentDrop.length) {
        return 'ComplaintViewDetails';
      }
      return null;
    },
    complaintsForCurrentDrop() {
      const result = (this.allComplaints.data || [])
        .filter((complaint) => {
          let flag = complaint.destination_location_id === this.selectedConsignee.location_id;
          if (this.configurations.multi_drop_at_gate_level && complaint.destination_gate_id
            && this.selectedConsignee.gate_id
          ) {
            flag = flag && complaint.destination_gate_id === this.selectedConsignee.gate_id;
          }
          return flag;
        });
      return result;
    },
    showComplaintsCreateView() {
      const isDestinationADepot = this.selectedConsignee.type === orderDestinationType.DEPOT;
      const isInboundPage = parseInt(this.$route.params.category, 10) === 1;
      const hasCreatePermission = this.$hasPermission('raise_complaints');

      // this check won't work because client id is stored in grn submitted by instead of user id
      const isGrnSubmittedByCurrentUser = this.selectedConsignee.grn_submitted_by.id === this.currentUser.id;
      return isDestinationADepot && this.isComplaintWindowActive && isInboundPage &&
        hasCreatePermission && !isGrnSubmittedByCurrentUser;
    },
    formatDate() {
      const date = new Date(this.selectedConsignee.complaint_submission_end_time);
      return `${moment(date).format('DD MMM YY, hh:mm A')}`;
    },
    isComplaintWindowActive() {
      if (this.selectedConsignee && this.selectedConsignee.complaint_submission_end_time) {
        const currentTime = moment();
        const complaintEndTime = moment(this.selectedConsignee.complaint_submission_end_time);
        return currentTime.isBefore(complaintEndTime);
      }
      return false;
    },
    getSvgIconIndent() {
      return this.currentDestination.is_cash_collected ? 'rupee' : 'rupee-error';
    },
    getClass() {
      return this.currentDestination.is_cash_collected ? 'status-green' : 'status-red';
    },
    considerSoldTo() {
      return this.$hasFeature('consider_sold_to_in_materials');
    },
    getRejectReason() {
      if (!this.selectedConsignee.is_transporter_updated &&
        this.selectedConsignee.epod_rejected && this.selectedConsignee.epod_rejected.reason) {
        return this.selectedConsignee.epod_rejected.reason;
      }
      return '';
    },
    lrAttachmentsLimit() {
      return (this.configurations && this.configurations.attachmentsLimit &&
        this.configurations.attachmentsLimit.lr) ? this.configurations.attachmentsLimit.lr : 5;
    },
    hasOverrideTypeWithVehicleConfig() {
      return (this.configurations && this.configurations.override_type_with_vehicle
      && this.orderDetails.order_type === this.orderType.CONTRACT) || false;
    },
    isMultiDropGateConfig() {
      return this.configurations && this.configurations.multi_drop_at_gate_level;
    },
    isOverLoaded() {
      if (this.allMaterials && this.allMaterials.materials_total) {
        const vehicleType = this.originalTruckWeightVolume;
        return (parseInt(vehicleType.volume, 10) < this.totalMaterial.volume ||
          parseInt(vehicleType.weight, 10) < this.totalMaterial.weight);
      }
      return false;
    },
    totalMaterial() {
      let totalMaterialWeightAndVolume = { weight: 0, volume: 0 };
      if (this.allMaterials && this.allMaterials.materials_total) {
        totalMaterialWeightAndVolume = Object.values(this.allMaterials.materials_total).reduce((materialObject, material) => {
          materialObject.weight += material.total_weight.value; // eslint-disable-line no-param-reassign
          materialObject.volume += material.total_volume.value; // eslint-disable-line no-param-reassign
          return materialObject;
        }, { weight: 0, volume: 0 });
      }
      if (this.preferredVolumeUnit !== VolumeUnits.CFT) { // dont change here
        totalMaterialWeightAndVolume.volume = new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, totalMaterialWeightAndVolume.volume);
      }
      return totalMaterialWeightAndVolume;
    },
    originalTruckWeightVolume() {
      const originalWeightVolume = {
        weight: 0,
        volume: 0,
      };
      if (!this.hasOverrideTypeWithVehicleConfig) {
        originalWeightVolume.weight = this.orderDetails.vehicle_type.kg;
        originalWeightVolume.volume = this.orderDetails.vehicle_type.cft;
      } else {
        originalWeightVolume.weight = this.orderDetails.vehicle.vehicle_filled_weight || 0;
        originalWeightVolume.volume = this.orderDetails.vehicle.vehicle_volume || 0;
      }

      if (this.preferredVolumeUnit && this.preferredVolumeUnit !== VolumeUnits.CFT && originalWeightVolume.volume) {
        originalWeightVolume.volume = new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, originalWeightVolume.volume);
      }
      return originalWeightVolume;
    },
    movementKey() {
      const map = { 2: 'primary', 1: 'secondary', 3: 'vendor', 4: 'return' };
      return map[this.orderDetails.movement] || 'primary';
    },
    addLrAttachmentPermission() {
      const movementSuffix = InvertedMovements[this.orderDetails.movement];
      return movementSuffix ? `indent_add_lr_attachment_${movementSuffix}` : null;
    },
    deleteLrAttachmentPermission() {
      const movementSuffix = InvertedMovements[this.orderDetails.movement];
      return movementSuffix ? `delete_lr_attachment_${movementSuffix}` : null;
    },
    addLrNumberPermission() {
      const movementSuffix = InvertedMovements[this.orderDetails.movement];
      return movementSuffix ? `indent_add_lr_number_${movementSuffix}` : null;
    },
    isDisableUpload() {
      const getCategory = this.Menu.find(val => val.id == this.category) || this.defaultSection;
      const orderStatus = this.orderDetails.status;
      const movementSuffix = InvertedMovements[this.orderDetails.movement];
      const restrictPermission = movementSuffix && `restrict_lr_attachment_post_dispatch_${movementSuffix}`;
      const isPostDispatch = (this.$statusUtil.isDispatched(orderStatus) || this.$statusUtil.isAfterDispatched(orderStatus))
        && !!restrictPermission && this.$hasPermission(restrictPermission);
      const lrUploadAllowed = this.configurations[`shipperUploadLr${getCategory.name}`]
        && this.addLrAttachmentPermission
        && this.$hasPermission(this.addLrAttachmentPermission)
        && !isPostDispatch;
      if (this.isConsolidatedEnabled) {
        const movementKey = this.movementKey;
        const podUploadAllowed = this.$hasPermission(`indent_add_pod_attachment_${movementKey}`);
        const othersUploadAllowed = this.$hasPermission('indent_upload_other_attachment');
        return !lrUploadAllowed && !podUploadAllowed && !othersUploadAllowed;
      }
      return !lrUploadAllowed;
    },
    isDisableDelete() {
      return !(this.deleteLrAttachmentPermission && this.$hasPermission(this.deleteLrAttachmentPermission));
    },
    showMaterialInvoiceOTP() {
      return this.orderDetails.status == 6 &&
      this.configurations.materialInvoiceEPODViaDMS &&
      this.orderDetails.movement === movementType.SECONDARY;
    },
    currentDestination() {
      const destinationLocationId = this.selectedConsignee.location_id;
      const destinationGateId = this.selectedConsignee.gate_id;
      if (destinationGateId) {
        return this.orderDetails.destinations.find(val =>
          val.location_id === destinationLocationId && val.gate_id === destinationGateId);
      }
      return this.orderDetails.destinations.find(val =>
        val.location_id === destinationLocationId);
    },
    deliveryNumberList() {
      if (this.orderDetails && !isEmpty(this.orderDetails.delivery_numbers)) {
        const destinationLocationId = this.selectedConsignee.location_id;
        const destinationIds = this.orderDetails.destinations.filter(val => val.location_id === destinationLocationId)
          .map(val => (this.selectedConsignee.type === this.sourceAndDestinationType.CONSIGNEE ? val.location_id : val.gate_id));
        return this.gatesList.map((gate) => {
          const gateDetails = { ...gate };
          const deliveryNumberDetail = this.orderDetails.delivery_numbers.filter(val =>
            val.source_id === gate.id && destinationIds.includes(val.ship_to_id));

          gateDetails.deliveryNumbers = deliveryNumberDetail &&
            deliveryNumberDetail.reduce((_acc, gateDeliveryNumberList) => {
              if (gateDeliveryNumberList.delivery_numbers) {
                let acc = _acc;
                acc = acc.concat(gateDeliveryNumberList.delivery_numbers.join(','));
                return acc;
              }
            }, []);
          gateDetails.deliveryNumbers = gateDetails.deliveryNumbers && gateDetails.deliveryNumbers.join(',');
          return gateDetails;
        });
      }
      return [];
    },
    canUpdateLrNumber() {
      // restirction of LR update by role for OEL after truckout
      let hideLRUpdate = false;
      if ([6, 23, 7].includes(this.orderDetails.status)) {
        hideLRUpdate = this.$hasPermission('hide_lr_update_post_truck_out');
      }
      const generateLr = this.orderDetails && this.orderDetails.transporter
        && this.orderDetails.transporter.generate_lr;
      return this.addLrNumberPermission && this.$hasPermission(this.addLrNumberPermission) && !hideLRUpdate && !generateLr;
    },
    canUpdateMaterialInvoiceLR() {
      let materialInvoiceLRUpdate = true;
      if ([23, 7].includes(this.orderDetails.status)) {
        materialInvoiceLRUpdate = false;
      }
      return materialInvoiceLRUpdate;
    },
    canShowConsignmentNumber() {
      return this.$hasPermission('update_consignment_number') &&
        (this.orderDetails && this.orderDetails.meta.courier) && this.orderDetails.meta.courier.add_manual_consignment_number;
    },
    hideConsignmentEdit() {
      return [23, 7, 8].includes(this.orderDetails.status);
    },
    destinations() {
      if (this.category == 1) {
        const accessDestinations =
        this.orderDetails.destinations &&
        this.orderDetails.destinations.filter(this.hasUserDepotAccess);
        if (this.isMultiDropGateConfig) {
          return uniqBy(accessDestinations, v => [v.location_id, v.gate_id].join());
        }
        return uniqBy(accessDestinations, 'location_id');
      }
      if (this.isMultiDropGateConfig && this.orderDetails.destinations) {
        if (this.considerSoldTo) {
          return (
            this.orderDetails.destinations &&
            uniqBy(
              this.orderDetails.destinations,
              v => [v.location_id, v.gate_id, v.sold_to_id].join(),
            )
          );
        }
        return uniqBy(this.orderDetails.destinations, v => [v.location_id, v.gate_id].join());
      }
      return (
        this.orderDetails.destinations &&
        uniqBy(this.orderDetails.destinations, 'location_id')
      );
    },
    isDispatched() {
      const orderStatus = this.orderDetails.status;
      return (
        this.$statusUtil.isDispatched(orderStatus) ||
        this.$statusUtil.isAfterDispatched(orderStatus)
      );
    },

    hasMaterialInvoice() {
      const hasInvoice =
        this.allMaterials &&
        this.allMaterials.materials.some(material => material.invoice.number);
      return (
        hasInvoice && this.$hasFeature('avoid_manual_material_invoice_update')
      );
    },

    /**
     * start of material details methods
     */
    getMaterialsByDestinationId() {
      const destinationLocationId = this.selectedConsignee.location_id;
      const soldToRefId = this.selectedConsignee.sold_details.reference_id;
      const destinationGateId = this.selectedConsignee.gate_id;
      if (this.allMaterials && this.allMaterials.materials.length) {
        // if (this.isMultiDropGateConfig && destinationGateId) { // * Commenting to make logic common for both multiDrop and vice versa
        let data;
        if (!this.isEximIndent) {
          if (this.considerSoldTo) {
            data = this.allMaterials.materials.filter((material) => {
              if (material.destination_gate_id && destinationGateId) {
                return material.destination_location_id === destinationLocationId &&
                    material.destination_gate_id === destinationGateId &&
                    material.destination.sold_to_reference_id === soldToRefId;
              }
              return material.destination_location_id === destinationLocationId &&
                  material.destination.sold_to_reference_id === soldToRefId;
            },
            );
          } else if (this.isMultiDropGateConfig && destinationGateId) {
            data = this.allMaterials.materials.filter((material) => {
              if (material.destination_gate_id && destinationGateId) {
                return material.destination_location_id === destinationLocationId
                        && material.destination_gate_id === destinationGateId;
              }
              return material.destination_location_id === destinationLocationId;
            },
            );
          } else {
            data = this.allMaterials.materials.filter(
              material => material.destination_location_id === destinationLocationId);
          }
        } else {
          // Exim has materials with the final destination(not the road destination)
          // So exim materials filter excludes sold_to_reference_id
          // Intentionally same code in if and else as one should not leave exim scenario
          // The above if part will be edited for non exim indents
          data = this.allMaterials.materials.filter(
            material => material.destination_location_id === destinationLocationId,
          );
        }
        return data;
        // }
      }
      return [];
    },
    showActualMaterials() {
      return this.$hasFeature('update_materials_for_ptl');
    },
    preferredVolumeUnit() {
      return this.$preferredVolumeUnit();
    },
    computeVolumeCalculationAndUnit() {
      const updatedVolumeData = {
        value: this.totalMaterial.volume - this.originalTruckWeightVolume.volume,
        unit: this.preferredVolumeUnit,
      };
      return updatedVolumeData;
    },
    materialsSummaryByDestinationId() {
      let materials = this.allMaterials &&
         this.allMaterials.materials_total[this.selectedConsignee.location_id];
      let summary = this.allMaterials &&
         this.allMaterials.material_summary &&
         this.allMaterials.material_summary[this.selectedConsignee.location_id];

      if (this.isMultiDropGateConfig) {
        let key = this.selectedConsignee.location_id.toString();
        key = this.selectedConsignee.gate_id ? key.concat(`_${this.selectedConsignee.gate_id.toString()}`) : key;

        summary = this.allMaterials &&
         this.allMaterials.material_summary &&
         this.allMaterials.material_summary[key];
        materials = this.allMaterials &&
         this.allMaterials.materials_total &&
         this.allMaterials.materials_total[key];
      }
      if (materials && summary) {
        if (summary.weight > 0) {
          materials.total_weight.value = summary.weight;
        }
        if (summary.volume > 0) {
          materials.total_volume.value = summary.volume;
        }
        if (summary.quantity > 0) {
          materials.total_quantity_nos.value ? materials.total_quantity_nos.value = summary.quantity : materials.total_quantity_cs.value = summary.quantity;
        }
      }
      return (materials || {});
    },
    actualSummaryByDestinationId() {
      let key = this.selectedConsignee.location_id;
      if (this.isMultiDropGateConfig && this.selectedConsignee.gate_id) {
        key = key.concat(`_${this.selectedConsignee.gate_id.toString()}`);
      }
      // eslint-disable-next-line array-callback-return
      const actualMaterials = this.allMaterials.actual_materials_total[key];
      const chargeableWeight = this.allMaterials.chargeable_weight;
      const isPackageExists = !isEmpty(this.allMaterials.packages);
      const isChargeableWeightsExists = !isEmpty(chargeableWeight) && ((chargeableWeight.csp_quantity + chargeableWeight.csp_weight + chargeableWeight.csp_volume) || 0) !== 0;
      const isActualQuantityExists = !isEmpty(actualMaterials) && ((actualMaterials.total_quantity + actualMaterials.total_weight + actualMaterials.total_volume) || 0) !== 0;
      if (isPackageExists && !isActualQuantityExists && !isChargeableWeightsExists) {
        return {};
      }
      const actualWeight = this.allMaterials.materials.reduce((total, material) => {
        let locationId = material && material.destination_location_id.toString();
        if (this.isMultiDropGateConfig && material.destination_gate_id) {
          locationId = locationId.concat(`_${material.destination_gate_id.toString()}`);
        }
        if (!total[locationId]) {
          // eslint-disable-next-line no-param-reassign
          total[locationId] = {
            total_weight: { value: 0, unit: '' },
            total_volume: { value: 0, unit: '' },
            total_quantity_nos: { value: 0, unit: '' },
            total_quantity_cs: { value: 0, unit: '' },
          };
        }
        total[locationId].total_weight.value += Number(material.weight.value) || 0;
        total[locationId].total_volume.value += Number(material.volume.value) || 0;
        let quantityUnit = material.quantity.unit.toLowerCase();
        if (isPackageExists && !material.quantity.actual_unit) {
          quantityUnit = (this.allMaterials.materials_total[key] && (this.allMaterials.materials_total[key].total_quantity_cs.unit || this.allMaterials.materials_total[key].total_quantity_nos.unit)) || quantityUnit;
        }
        if (this.qtyUnits[quantityUnit] === units.PIECES) {
          total[locationId].total_quantity_nos.value += material.quantity.value;
          total[locationId].total_quantity_nos.unit = 'pieces';
        } else {
          total[locationId].total_quantity_cs.value += material.quantity.value;
          total[locationId].total_quantity_cs.unit = 'cases';
        }
        return total;
      }, {});

      return actualWeight[key];
    },
    invoiceAmount() {
      return this.getMaterialsByDestinationId
        ? this.getMaterialsByDestinationId.reduce(
          (acc, con) => acc + con.invoice.amount,
          0,
        )
        : 0;
    },
    isGateWiseLrConfig() {
      return (
        ((this.orderDetails && this.orderDetails.lr_type) ||
        (this.configurations && this.configurations.lorry_receipt_generate_category)) ===
          LorryReceiptGenerationCategory.GATE_WISE
      );
    },
    isDivisionGroupingDisabled() {
      return this.configurations && this.configurations.disable_division_grouping;
    },
    materialsList() {
      if (this.isGateWiseLrConfig) {
        const materialDetails =
          this.getMaterialsByDestinationId &&
          this.getMaterialsByDestinationId.map((material) => {
            const pickupSourceDetails = this.depots.find(
              val => val.id === material.source_location_id,
            );
            if (pickupSourceDetails) {
              const gateDetails = pickupSourceDetails.gates.find(
                gate => gate.id === material.source_gate_id,
              );
              return { ...material, ...{ gateName: gateDetails.name } };
            }
            const source = this.orderDetails.sources.find(
              source => source.gate_id === material.source_gate_id,
            );
            return { ...material, ...{ gateName: source && source.details.name } };
          });
        return materialDetails && groupBy(materialDetails, 'source_gate_id');
      }
      if (this.isDivisionGroupingDisabled) {
        return this.getMaterialsByDestinationId && this.getMaterialsByDestinationId.length
          ? { all: this.getMaterialsByDestinationId }
          : {};
      }
      return (
        this.getMaterialsByDestinationId &&
        groupBy(this.getMaterialsByDestinationId, item =>
          `${item.division}^${!isEmpty(item.business_division) && item.business_division}`)
      );
    },

    hasEpodMaterialDefect() {
      const epodValues = this.getMaterialsByDestinationId.reduce((acc, arrayItem) => {
        acc.shortage += arrayItem.epod.shortage_count;
        acc.damage += arrayItem.epod.damage_count;
        acc.cartonDamage += arrayItem.epod.carton_damage_count;
        return acc;
      }, { shortage: 0, damage: 0, cartonDamage: 0 });

      const epodSumValue = Object.values(epodValues);
      return epodSumValue.some(item => item > 0);
    },

    hasMaterials() {
      return !!Object.keys(this.materialsList).length;
    },
    /**
     * end of material details methods
     */
    /**
     * start of delivery numbers methods
     */
    // gate list of the current selected consignee
    gatesList() {
      const destinations = (this.orderDetails && this.orderDetails.destinations) || [];
      const destinationsList = destinations.filter(val => val.location_id === this.selectedConsignee.location_id);
      const pickupSourceDetails = this.depots.find(val => val.id === this.selectedConsignee.source_location_id);
      if (pickupSourceDetails) { // DEPOT
        let gatesList = destinationsList.map(destination => destination.source_gate_id);
        gatesList = uniq(gatesList);
        return gatesList.map((pickupGate) => {
          const gateDetails = pickupSourceDetails.gates.find(gate => gate.id === pickupGate);
          return {
            id: pickupGate,
            name: gateDetails && gateDetails.name || '',
            reference_id: gateDetails && gateDetails.reference_id || '',
            type: sourceAndDestinationType.DEPOT,
          };
        });
      } // VENDOR DEPOT or CONSIGNEE
      const sourceDetails = this.orderDetails &&
          this.orderDetails.sources.find(val => val.location_id === this.selectedConsignee.source_location_id); // only one source for a destination
      return [{
        id: sourceDetails.gate_id || sourceDetails.location_id,
        name: sourceDetails && sourceDetails.details.name,
        reference_id: sourceDetails && sourceDetails.details.reference_id,
        type: sourceDetails.type,
      }];
    },
    /**
     * end of delivery numbers method
     */
    dropGatesList() {
      const destinations = (this.orderDetails && this.orderDetails.destinations) || [];
      const destinationsList = destinations.filter(val => val.location_id === this.selectedConsignee.location_id);
      const dropDestinationDetails = this.depots.find(val => val.id === this.selectedConsignee.location_id);
      if (dropDestinationDetails) { // DEPOT
        let gatesList = destinationsList.map(destination => destination.gate_id);
        gatesList = uniq(gatesList);

        return gatesList.map((dropGate) => {
          const gateDetails = dropDestinationDetails.gates.find(gate => gate.id === dropGate);
          return {
            id: dropGate,
            name: gateDetails.name,
            reference_id: gateDetails.reference_id,
            type: sourceAndDestinationType.DEPOT,
          };
        });
      } // VENDOR DEPOT or CONSIGNEE
      const destinationDetails = this.orderDetails &&
          this.orderDetails.destinations.find(val => val.location_id === this.selectedConsignee.location_id); // only one source for a destination
      return [{
        id: destinationDetails.gate_id || destinationDetails.location_id,
        name: destinationDetails && destinationDetails.details && destinationDetails.details.name,
        reference_id: destinationDetails && destinationDetails.details && destinationDetails.details.reference_id,
        type: destinationDetails.type,
      }];
    },
    soldToIdMap() {
      return this.orderDetails.destinations.reduce((_all, cur) => {
        const all = _all;
        let key = '';
        if (cur.source_gate_id) {
          key += `${cur.source_gate_id}`;
        } else {
          key += `${cur.source_location_id}`;
        }
        if (cur.gate_id) {
          key += `_${cur.gate_id}`;
        } else {
          key += `_${cur.location_id}`;
        }
        all[key] = cur.sold_to_id;
        return all;
      }, {});
    },
    getConsignmentNumber() {
      return (destination) => {
        if (this.transporterBookingData !== null) {
          const destinationList = this.orderDetails.destinations
            .filter((dest) => {
              if (this.isMultiDropGateConfig) {
                if (dest.gate_id && destination.gate_id && dest.gate_id.toString() &&
                 destination.gate_id.toString()) {
                  return dest.gate_id.toString() === destination.gate_id.toString();
                }
              }
              return dest.location_id.toString() === destination.location_id.toString();
            })
            .map(dest => (dest.gate_id && dest.gate_id.toString()) ||
             (dest.location_id && dest.location_id.toString()));
          destinationList.push(destination.location_id.toString());
          const consignmentDetails = this.transporterBookingData.transporterBookings.filter(data =>
            destinationList.includes(data.consignee_id.toString()));
          return consignmentDetails;
        }
        return {};
      };
    },
    shipToBasedOnLRCategory() {
      const soldDetails = this.selectedConsignee.type === this.types.DEPOT && this.selectedConsignee.gate_id === this.selectedConsignee.sold_to_id
        ? this.$depot.otherGate(this.selectedConsignee.sold_to_id, this.selectedConsignee.location_id)
        : this.selectedConsignee.details;
      return `${soldDetails.reference_id} - ${soldDetails.name} - ${soldDetails.city}`;
    },
    soldToBasedOnLRCategory() {
      return `${this.selectedConsignee.sold_details.reference_id} - ${this.selectedConsignee.sold_details.name} - ${this.selectedConsignee.sold_details.city}`;
    },
    showMaterialAccess() {
      return this.$hasFeature('enable_material_excess');
    },
    showComplaints() {
      const permissionEnabled = this.$hasPermission('view_customer_complaints') || this.$hasPermission('raise_complaints');
      const isGrnSubmitted = this.selectedConsignee.grn_submitted_by && this.selectedConsignee.grn_submitted_by.id
        && this.selectedConsignee.grn_submitted_at;
      return !!(this.$hasFeature('customer_complaints') && permissionEnabled && isGrnSubmitted);
    },
  },
  methods: {
    routeToIndentPath() {
      return { path: '/indents' };
    },
    async approveDestinationPOD() {
      try {
        const orderNo = this.orderDetails.order_number;
        const locationId = this.selectedConsignee.location_id;
        const gateId = (this.selectedConsignee && this.selectedConsignee.gate_id) || null;

        const hasConfirm = await this.$PromptConfirm(this.$getConst('POD_APPROVAL_PROMPT', { firstArgument: this.selectedConsignee.details.name }), 'Confirmation', {
          confirmButtonText: 'Approve',
          cancelButtonText: 'Cancel',
          lockScroll: true,
          showClose: false,
          center: true,
          dangerouslyUseHTMLString: true,
        });
        if (hasConfirm === 'confirm') {
          const result = await this.$store.dispatch('order/approveDestinationPOD', {
            orderNo,
            locationId,
            gateId,
          });

          if (result.success) {
            const backendIsLastDestination = result && result.isFinalDestination;
            let updatedDestination = null;

            if (result.data && result.data.destinations && Array.isArray(result.data.destinations)) {
              updatedDestination = result.data.destinations.find(dest =>
                dest.location_id === this.selectedConsignee.location_id ||
              (this.selectedConsignee.type === 1 &&
              (!this.selectedConsignee.gate_id || dest.gate_id === this.selectedConsignee.gate_id)),
              );
            }
            if (updatedDestination) {
              this.updateSelectedConsignee(updatedDestination);
            }
            if (backendIsLastDestination) {
              this.$router.push(this.routeToIndentPath());
            }
            this.$success({ message: result.message });
            await this.$store.dispatch('order/find', orderNo);
          } else {
            this.$error({ message: result.error || 'Failed to approve destination POD.' });
          }
        }
      } catch (error) {
        this.$error({ message: 'Error approving destination POD.' });
      }
    },
    openLrSummarySlider(){
      this.viewLRSummarySlider = true;
    },
    getDataInterpolatedText(subject, data) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (key === 'timestamp') {
          acc = acc.replaceAll(`{${key}}`, `<strong>${value}</strong>`);
        } else {
          acc = acc.replaceAll(`{${key}}`, value);
        }
        return acc;
      }, subject);
    },
    async fetchComplaints() {
      const orderId = this.$route.params.orderId || this.$route.params.orderNo;
      if (orderId) {
        await this.$store.dispatch('order/complaints', orderId);
      }
    },
    getFilteredAttachments(attachments) {
      if (!attachments.length) {
        return [];
      }
      return attachments.map(att => ({
        id: att.id,
        name: att.name,
        url: att.attachment_url,
      }));
    },
    getSourceConsigneeDetails(destination) {
      if (destination.location_id === destination.source_location_id && [LorryReceiptGenerationCategory.LEGAL_ENTITY_WISE, LorryReceiptGenerationCategory.CONSIGNEE_WISE].includes(this.configurations.lorry_receipt_generate_category)) {
        const gateName = this.sourceHasGate(destination.source_gate_id, destination.source_location_id, destination);
        return gateName;
      }
      return destination.details.name;
    },
    sourceHasGate(gateId, locationId, source) {
      const otherGate = this.$depot.otherGate(gateId, locationId);
      return gateId && source.type === this.types.DEPOT && otherGate && otherGate.name
        ? otherGate.name
        : source.details.name;
    },
    getLrNumberDetails(destination) {
      return this.getLrNumberList(destination);
    },
    getConsigneeDetails(destination) {
      const consigneeDetails = `${destination.details.reference_id} - ${destination.details.city}`;
      const gateDetailsMap = {};
      let destinations = [];
      if (this.isMultiDropGateConfig) {
        destinations = [destination];
      } else {
        destinations = this.orderDetails.destinations.filter(ele =>
          ele.location_id === destination.location_id,
        );
      }
      const depotDetails = this.depots.find(val => val.id === destination.location_id);
      destinations.forEach((doc) => {
        if (depotDetails && depotDetails.gates) {
          const value = depotDetails.gates.find(gate => gate.id === doc.gate_id);
          if (value) {
            gateDetailsMap[value.reference_id] = value.reference_id;
          }
        }
      });
      const gateDetails = gateDetailsMap && !isEmpty(gateDetailsMap) ?
        Object.keys(gateDetailsMap) :
        [];
      return gateDetails.length ? `${consigneeDetails} - ${gateDetails.join(', ')}` : consigneeDetails;
    },
    getSourceIdName(type, locationId, gateId, details) {
      if (type === this.sourceAndDestinationType.DEPOT) {
        const depotDetails = this.depots.find(val => val.id === locationId);
        const gateDetails = depotDetails.gates.find(gate => gate.id === gateId);
        return {
          id: gateId,
          name: gateDetails.name,
        };
      }
      return {
        id: type === this.sourceAndDestinationType.CONSIGNEE ? locationId : gateId,
        name: details.details.name,
      };
    },
    hasUserDepotAccess(destination) {
      const hasDepotAccess =
      this.currentUserDepots && this.currentUserDepots.length;
      const depotIds = this.currentUserDepots.map(_depot => _depot.id);
      return destination.type === this.sectionType.DEPOT &&
      hasDepotAccess ? depotIds.includes(destination.location_id) : true;
    },
    hasMaterialsByDestinationId(destination) {
      const destinationId = destination.location_id;
      if (this.allMaterials && this.allMaterials.materials.length) {
        return this.allMaterials.materials.some(
          material => material.destination_location_id === destinationId,
        );
      }
      return false;
    },
    getLrAttachmentsByDestination(destination) {
      if(!isEmpty(destination)) {
        return (
          this.attachments &&
          this.attachments.get(destination.gate_id || destination.location_id)
        );
      }
      return Array.from(this.attachments.values()).flat();
    },

    async fetchMaterials() {
      // fetch the materials
      const inbound = this.category == 1; // condition for inbound
      const params = {
        inbound: false,
      };
      if (inbound) {
        params.inbound = true;
      }
      await this.$store.dispatch('order/materials', {
        orderId: this.$route.params.orderId,
        params,
      });
      if (this.configurations && this.configurations.manual_lr === true) {
        await this.$store.dispatch('order/getUpdatedOrder', {
          orderId: this.$route.params.orderId,
        });
        await this.$store.dispatch(
          'order/lorryReceipt',
          this.$route.params.orderId,
        );
      }
    },
    epodRejectReason(reason) {
      if (this.selectedConsignee.epod_rejected) {
        this.selectedConsignee.epod_rejected.reason = reason;
      } else {
        this.selectedConsignee.epod_rejected = {
          reason,
        };
      }
    },
    /** start of consignee left pane actions */
    onChangeConsignee(data) {
      if (this.isReturnMaterialsEnabled && !isEmpty(this.unsavedReturnMaterialDetails) && !this.returnMaterialWarnedOnce
        && (data !== this.selectedConsignee)) {
        this.$warningMessage({ message: this.$t('RETURN_MATERIALS_UNSAVED_WARNING') });
        this.$store.commit('order/RETURN_MATERIALS_WARNED_ONCE', true);
      } else if (this.userEdited && !this.warnedOnce && (data !== this.selectedConsignee)) {
        this.$warningMessage({ message: this.$getConst('EPOD_VALUE_CHANGED') });
        this.$store.commit('order/HAS_WARNED_ONCE', true);
      } else if (this.showComplaints && this.hasUnsavedComplaints && !this.unsavedComplaintsWarnedOnce) {
        this.$warningMessage({ message: this.$t('UNSAVED_COMPLAINTS_WARNING') });
        this.$store.commit('order/UNSAVED_COMPLAINTS_WARNED_ONCE', true);
      } else {
        if (this.$refs.material_details) {
          this.$refs.material_details.clearReturnMaterialData();
        }
        this.selectedConsignee = data;
        this.showSaveButton();
        this.$emit('setGA', 'Click', 'Consignee Card');
      }
    },
    /**
     * end of consignee left pane actions
     */

    fixTwoDigits(val) {
      return parseFloat(val.toFixed(2));
    },
    /**
     * start of LR number related methods
     */

    async getLrDetails() {
      await this.$store.dispatch(
        'order/lorryReceipt',
        this.$route.params.orderId,
      );
    },
    /**
     * end of LR number related methods
     */
    openPreviewSlider(destination) {
      const target = destination || this.selectedConsignee;
      if (this.isConsolidatedEnabled) {
        this.activeDestination = target;
        this.activeDestinationIndex = this.destinations.indexOf(target);
        this.previewDrawerVisible = true;
        return;
      }
      if (this.$hasFeature('pod_rejection_by_ai') && this.getPodAiDestStatus(target) === 'AI_REJECTED') {
        this.openPodAiSlider(target);
      } else {
        this.selectedConsignee = target;
        this.viewAttachmentsDetailsSlider = true;
      }
    },
    onDrawerAddAttachment(destinationIndex) {
      this.activeDestination = this.destinations[destinationIndex];
      this.uploadModalVisible = true;
    },

    /**
     * start of LR attachments related methods
     */
    async addAttachments(file, destination) {
      if (this.isConsolidatedEnabled) {
        this.activeDestination = destination;
        this.uploadModalVisible = true;
        return;
      }
      this.selectedConsignee = destination;
      try {
        const formData = new FormData();
        formData.append('attachment', file.raw);
        const result = await this.$store.dispatch(
          'order/addAttachment',
          formData,
        );
        if (result && result.error) {
          this.$error({ message: result.error || this.$getErr('UPLOAD_FAILS')});
        } else {
          this.saveLorryReceipt(result);
        }
      } catch (err) {
        const apiError = err && err.data && (err.data.messages || (err.data.errors && err.data.errors[0] && err.data.errors[0].messages));
        this.$error({ message: apiError || this.$getErr('UPLOAD_FAILS') });
      }
    },
    async onAttachmentUploaded() {
      this.uploadModalVisible = false;
      await this.refreshV2AttachmentsCount();
      if (this.$refs.documentPreviewDrawer) {
        await this.$refs.documentPreviewDrawer.fetchAttachments();
      }
    },
    async onAttachmentDeleted() {
      this.previewDrawerVisible = false;
      await this.getLrAttachments();
      await this.refreshV2AttachmentsCount();
    },
    async removeAttachments(file) {
      try {
        const data = {
          indent_id: this.$route.params.orderId,
          consignee_id: this.selectedConsignee.location_id,
          file_id: file.id,
        };
        const result = await this.$store.dispatch('order/removeAttachment', {
          data,
        });
        if (result && result.error) {
          this.$error({ message: this.$getErr('DELETE_FILE_FAILS') });
        } else {
          await this.getLrAttachments(true);
        }
      } catch (err) {
        this.$error({ message: this.$getErr('DELETE_FILE_FAILS') });
      }
    },
    async rejectAttachments(rejectsData) {
      try {
        const data = {
          consigneeId: this.selectedConsignee.location_id,
          reason: rejectsData.lrRejectedReason,
        };
        const result = await this.$store.dispatch('order/rejectLRAttachment', {
          orderId: this.$route.params.orderId,
          attchmentId: rejectsData.attachmentId,
          data,
        });
        if (result && result.error) {
          this.$error({ message: this.$getErr('LR_REJECT_FAILS') });
        } else {
          await this.getLrAttachments(true);
        }
      } catch (err) {
        this.$error({ message: this.$getErr('LR_REJECT_FAILS') });
      }
    },
    async saveLorryReceipt(result) {
      try {
        const lorryReceipts = {
          destination_id: this.selectedConsignee.location_id,
          ...!isEmpty(this.selectedConsignee.gate_id) ? { gate_id: this.selectedConsignee.gate_id } : {},
          source_location_id: this.selectedConsignee.source_location_id,
          lr_attachment_ids: [result.id],
        };
        const orderId = this.$route.params.orderId;
        const response = await this.$store.dispatch('order/saveLorryReceipt', {
          orderId,
          data: [lorryReceipts],
        });
        if (response && response.error) {
          this.$error({ message: response.error || this.$getErr('UPLOAD_FAILS') });
        } else {
          await this.getLrAttachments(true);
        }
      } catch (err) {
        this.$error({ message: this.$getErr('UPLOAD_FAILS') });
      }
    },
    async getLrAttachments(triggerPodRefetch = false) {
      const result = await this.$store.dispatch('order/getLrAttachments', {
        orderId: this.orderDetails.order_number,
      });
      if (result.data) {
        this.attachments = new Map();
        this.lrAttachmentsObj = {};
        for (let index = 0; index < result.data.destinations.length; index++) {
          const destinationData = result.data.destinations[index];
          const attachmentData = destinationData.lr_attachment_ids.map(
            attachment => ({
              name: attachment.content_file_name,
              url: attachment.metadata.location,
              summary: attachment.summary,
              // eslint-disable-next-line no-underscore-dangle
              id: attachment._id,
            }),
          );
          const destinationId =
            destinationData.gate_id || destinationData.location_id;
          if (!this.attachments.get(destinationId)) {
            this.attachments.set(destinationId, attachmentData);
            this.$set(this.lrAttachmentsObj, destinationId, attachmentData);
          }
        }
        this.$emit('lr-attachments-updated', this.lrAttachmentsObj);
        this.$store.commit('order/SET_LR_ATTACHMENTS_MAP', { ...this.lrAttachmentsObj });
        if (triggerPodRefetch && this.$hasFeature('pod_rejection_by_ai')) {
          this.fetchPodAiValidationStatus();
        }
      }
    },
    async refreshV2AttachmentsCount() {
      if (!this.isConsolidatedEnabled) return;
      const response = await this.$store.dispatch('order/listV2Attachments', {
        orderNo: this.orderDetails.order_number,
      });
      const list = Array.isArray(response) ? response : (response && response.data) || [];
      list.forEach((destData) => {
        const dest = this.orderDetails.destinations.find(
          d => (d.gate_id || d.location_id)
            && (d.gate_id || d.location_id).toString() === destData.destination_id,
        );
        if (!dest) return;
        const destinationId = dest.gate_id || dest.location_id;
        const allFiles = [
          ...(destData.lr_attachments || []),
          ...(destData.pod_attachments || []),
          ...(destData.other_attachments || []),
        ];
        const mapped = allFiles.map(a => ({
          // eslint-disable-next-line no-underscore-dangle
          id: a._id,
          name: a.content_file_name,
          url: a.metadata && a.metadata.location,
        }));
        this.attachments.set(destinationId, mapped);
        this.$set(this.lrAttachmentsObj, destinationId, mapped);
      });
      this.attachments = new Map(this.attachments);
      this.$store.commit('order/SET_LR_ATTACHMENTS_MAP', { ...this.lrAttachmentsObj });
    },
    async submitOtp(destinationId) {
      try {
        const orderNo = this.orderDetails.order_number;
        const otp = this.otp;
        const res = await this.$store.dispatch('order/approveOTP', {
          orderNo,
          otp,
          destinationId,
        });
        if (res && res.status === 204) {
          this.$success({ message: 'OTP SUBMITTED SUCCESSFULLY' });
          this.$emit('routeToIndentsList');
          this.showOtpPopup = false;
        } else {
          this.$error({ message: this.$getErr('OTP_FAILED') });
        }
      } catch (err) {
        this.$error({ message: this.$getErr('OTP_FAILED') });
      }
    },
    async resendOtp(destinationId) {
      try {
        const orderNo = this.orderDetails.order_number;
        const res = await this.$store.dispatch('order/resendOTP', {
          orderNo,
          destinationId,
        });
        if (res && res.status === 204) {
          this.$success({ message: 'OTP RESENT PROCESSED' });
          this.$emit('routeToIndentsList');
        } else if (res && res.error === 'ERR_OTP_GENERATION_EXPIRED') {
          this.$error({ message: this.$getErr('OTP_GENERATION_EXPIRED') });
        } else {
          this.$error({ message: this.$getErr('OTP_RESENT_FAILED') });
        }
      } catch (err) {
        this.$error({ message: this.$getErr('OTP_RESENT_FAILED') });
      }
    },
    isMaterialTabSelected() {
      return this.currentSelectedTab === 'material';
    },
    canEnterOtp() {
      let canEnable = false;
      if (this.category === 1) {
        // condition for inbound
        canEnable = (
          this.$hasFeature('epod_otp_verification') &&
              this.orderDetails.status === this.$statusUtil.getStatusId('delivery_pending')
              && this.orderDetails.delivery_pending_reason ===
              deliveryPendingReasonsMapping.OTP_VERIFICATION
        );
        if (canEnable && this.configurations.disable_otp_for_zero_epod) {
          canEnable = this.hasEpodMaterialDefect;
        }
      }
      return canEnable;
    },

    otpVerified(destinationId) {
      const destinations = this.orderDetails.destinations
        .filter(destination => destination.location_id.toString()
            === destinationId.toString());
      if (destinations.length) {
        return destinations.some(dest => dest.otp_verified);
      }
      return false;
    },

    /**
     * end of LR attachments related methods
     */
    /**
     * start of consignment number related methods
     */

    async getTransporterBooking() {
      const result = await this.$store.dispatch('order/trackingList', { id: this.orderDetails.order_number });
    },
    /**
     * end of consignment number related methods
     */

    showSaveButton() {
      const destinations = this.orderDetails && this.orderDetails.destinations;
      let dropIndex;
      this.canEPODEditAble = true;
      if (this.selectedConsignee.gate_id) {
        dropIndex = (destinations).map((destination, index) =>
          ((destination.gate_id === this.selectedConsignee.gate_id) ?
            index : undefined)).filter(x => x >= 0)[0];
      } else {
        dropIndex = (destinations).map((destination, index) =>
          ((destination.location_id === this.selectedConsignee.location_id) ?
            index : undefined)).filter(x => x >= 0)[0];
      }
      if ((destinations[dropIndex] && destinations[dropIndex].grn_submitted_by
      && destinations[dropIndex].grn_submitted_by.entity_type) === auditPlatform.TRANSPORTER &&
          this.$hasPermission('epod_rejection')) {
        this.canEPODEditAble = false;
        return false;
      }
      // based on approval status we will override all existing grn editability check
      this.canEPODEditAble = OrderUtils.grnEditabilityCheckBasedOnApprovalStatus(
        this.orderDetails,
        this.canEPODEditAble,
      );
      return this.canEditGrn;
    },
    toLowerCaseKeys(obj) {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key.toLowerCase()] = value;
        return acc;
      }, {});
    },
    async getMaterialExcess() {
      const excessMaterialData = await this.$store.dispatch('order/getExcessMaterials', { orderId: this.$route.params.orderId });
      if (excessMaterialData && excessMaterialData.length > 0) {
        this.excessMaterials = excessMaterialData;
      } else {
        this.excessMaterials = [];
      }
    },
    getConvertedClientVolume(volume) {
      const volumeInClientUnit = new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, volume);
      return volumeInClientUnit;
    },
    updateSelectedConsignee(updatedDestination) {
      this.selectedConsignee = { ...this.selectedConsignee, ...updatedDestination };
    },
    async fetchPodAiValidationStatus() {
      try {
        const result = await this.$store.dispatch('podAiValidation/fetchStatus', {
          orderNo: this.orderDetails.order_number,
        });
        this.podAiValidationStatus = result;
      } catch (err) {
        // silent fail — non-critical
      }
    },
    getPodAiDestStatus(destination) {
      if (!this.podAiValidationStatus || !this.podAiValidationStatus.destinations) return null;
      const locId = destination.location_id && destination.location_id.toString();
      const found = this.podAiValidationStatus.destinations.find(
        d => d.destination_id && d.destination_id.toString() === locId,
      );
      return found ? found.status : null;
    },
    getPodAiRejectionReasons(destination) {
      if (!this.podAiValidationStatus || !this.podAiValidationStatus.destinations) return [];
      const locId = destination.location_id && destination.location_id.toString();
      const found = this.podAiValidationStatus.destinations.find(
        d => d.destination_id && d.destination_id.toString() === locId,
      );
      return (found && found.rejection_reasons) || [];
    },
    openPodAiSlider(destination) {
      this.selectedConsignee = destination;
      this.podAiSliderDestination = destination;
      this.viewPodAiSlider = true;
    },
  },
  beforeDestroy() {
    this.$store.commit('order/SET_CUSTOMER_COMPLAINTS', []);
  },
  mounted() {
    this.qtyUnits = { ...this.toLowerCaseKeys({ ...this.configurations.customMaterialUom, ...quantityUnits }) };
  },
};
</script>

<style lang="scss">
.pod-ai-rejected-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #fdecea;
  color: #c0392b;
  font-size: 1.2rem;
  font-weight: 500;
  padding: 8px 10px;
  border-top: 1px solid #f5c6c2;
  border-radius: 0 0 2px 2px;
  cursor: pointer;
}

.pod-ai-approved-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #eafaf1;
  color: #1e8449;
  font-size: 1.2rem;
  font-weight: 500;
  padding: 8px 10px;
  border-top: 1px solid #a9dfbf;
  border-radius: 0 0 2px 2px;

  svg {
    fill: #1e8449;
  }
}

.card-list.is-attachment {
  overflow: hidden;
}
.pod-ai-validation-box {
  background-color: #2c2c2c;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 20px 22px;
  color: #fff;

  .pod-ai-validation-header {
    display: flex;
    align-items: center;
    font-size: 1.5rem;
    margin-bottom: 10px;
  }

  .pod-ai-reason-item {
    display: flex;
    align-items: center;
    font-size: 1.3rem;
    margin-top: 10px;
    color: #ff6b6b;
  }
}
</style>
